/**
 * SEO Utilities
 * Helper functions for generating structured data and SEO elements
 */

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.werkix.io';

/**
 * Generate breadcrumb structured data
 * @param {Array} items - Array of {name, url} objects
 * @returns {Object} BreadcrumbList schema
 */
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url,
    })),
  };
};

/**
 * Generate article structured data for blog posts
 * @param {Object} post - Blog post data
 * @returns {Object} Article schema
 */
export const generateArticleSchema = (post) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.heading || post.text?.text?.substring(0, 100),
    "description": post.description || post.text?.text?.substring(0, 200),
    "image":
      post.image ||
      post.images?.[0]?.url ||
      post.content?.contentEntities?.[0]?.entityLocation,
    "datePublished": post.created?.time || post.createdAt,
    "dateModified": post.created?.time || post.createdAt,
    "author": {
      "@type": "Organization",
      "name": "Werkix LLC",
    },
    "publisher": {
      "@type": "Organization",
      "name": "Werkix LLC",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo512.png`,
      },
    },
  };
};

/**
 * Generate job posting structured data
 * @param {Object} job - Job data
 * @returns {Object} JobPosting schema
 */
export const generateJobPostingSchema = (job) => {
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "identifier": {
      "@type": "PropertyValue",
      "name": "Werkix",
      "value": job.id.toString(),
    },
    "datePosted": job.posted,
    "validThrough": job.deadline,
    "employmentType": job.employmentType,
    "workHours": job.workArrangement,
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Werkix LLC",
      "sameAs": "https://www.werkix.io",
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "MW",
        "addressLocality": "Blantyre",
      },
    },
    "baseSalary": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": {
        "@type": "QuantitativeValue",
        "value": "Competitive",
      },
    },
  };
};

/**
 * Generate website schema with search action
 * @returns {Object} WebSite schema
 */
export const generateWebsiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Werkix",
    "url": SITE_URL,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/careers?search={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

/**
 * Generate enhanced organization schema
 * @returns {Object} Organization schema
 */
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Werkix LLC",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo512.png`,
    "description": "Werkix empowers African tech talent by connecting skilled professionals with global opportunities. Specializing in software development, IT support, and tech consulting services.",
    "foundingDate": "2020",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Blantyre",
      "addressCountry": "MW",
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+265-888-007-888",
      "email": "hello@werkix.io",
      "contactType": "customer service",
      "areaServed": "Worldwide",
    },
    "sameAs": [
      "https://www.linkedin.com/company/88634432",
      "https://www.facebook.com/profile.php?id=100086435430979",
      "https://www.instagram.com/werkix.io/",
    ],
    "serviceArea": {
      "@type": "Place",
      "name": "Global",
    },
    "areaServed": "Worldwide",
    "industry": "Information Technology",
    "knowsAbout": [
      "Software Development",
      "IT Support",
      "Web Development",
      "Cybersecurity",
      "Tech Recruitment",
      "Talent Placement",
    ],
  };
};

/**
 * Generate person schema for team members
 * @param {Object} member - Team member data
 * @returns {Object} Person schema
 */
export const generatePersonSchema = (member) => {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": member.name,
    "jobTitle": member.position,
    "description": member.statement,
    "image": member.image,
    "email": member.contact.email,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": member.contact.location,
    },
    "worksFor": {
      "@type": "Organization",
      "name": "Werkix LLC",
    },
  };
};
